import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import useAuthentication from "../../hooks/useAuthentication";
import SyncConstants from "../../constants/SyncConstants";
import MinutesToTime from "./MinutesToTime";
import stylelist from "stylelist";

const MonthlyBreakdown = ({ project_id }) => {
    const Auth = useAuthentication();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [monthsToShow, setMonthsToShow] = useState(12);
    const [collapsedProjects, setCollapsedProjects] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState("");
    const [hideEmptyProjects, setHideEmptyProjects] = useState(true);

    // Load data from API
    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Convert monthsToShow to backend format
            const monthsParam = monthsToShow === 0 ? 'all' : monthsToShow.toString();

            const response = await axios({
                method: "get",
                auth: Auth.getAuthObject(),
                url: SyncConstants["syncBaseURL"] + "expenseProjects/breakdown",
                params: {
                    project_id: project_id,
                    months: monthsParam,
                    hide_empty: hideEmptyProjects ? '1' : '0'
                },
                responseType: 'json'
            });

            setData(response.data.data);
        } catch (err) {
            setError("Fehler beim Laden der Daten");
            console.error("Error loading monthly breakdown:", err);
        } finally {
            setLoading(false);
        }
    }, [project_id, monthsToShow, hideEmptyProjects]);

    useEffect(() => {
        loadData();
    }, [project_id, monthsToShow, hideEmptyProjects]);

    // Get months from backend (already filtered by monthsToShow)
    const filteredMonths = useMemo(() => {
        return data?.available_months || [];
    }, [data]);

    // Format month for display
    const formatMonth = useCallback((monthString) => {
        const [year, month] = monthString.split('-');
        const monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun',
                          'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
        return {
            short: monthNames[parseInt(month) - 1],
            year: year
        };
    }, []);

    // Calculate max value for bar chart scaling using pre-calculated totals
    const maxMonthlyValue = useMemo(() => {
        if (!data?.projects) return 0;
        let max = 0;
        data.projects.forEach(project => {
            filteredMonths.forEach(month => {
                const value = project.monthly_totals_with_children?.[month] || 0;
                if (value > max) max = value;
            });
        });
        return max;
    }, [data, filteredMonths]);

    // Toggle project collapse
    const toggleProject = useCallback((projectId) => {
        setCollapsedProjects(prev => {
            const next = new Set(prev);
            if (next.has(projectId)) {
                next.delete(projectId);
            } else {
                next.add(projectId);
            }
            return next;
        });
    }, []);

    // Filter projects by search term only (backend handles empty filtering)
    const filteredProjects = useMemo(() => {
        if (!data?.projects) return [];

        // Note: Backend already filtered empty projects if hideEmptyProjects was passed
        // We only need to handle search filtering here
        if (!searchTerm.trim()) {
            return data.projects;
        }

        const term = searchTerm.toLowerCase();
        const visibleProjectIds = new Set();

        // First, find all matching projects
        data.projects.forEach((project, index) => {
            if (project.project_title.toLowerCase().includes(term) ||
                (project.breadcrumb && project.breadcrumb.toLowerCase().includes(term))) {
                visibleProjectIds.add(project.project_id);

                // Also mark all parents as visible (to maintain hierarchy)
                for (let i = index - 1; i >= 0; i--) {
                    const potentialParent = data.projects[i];
                    if (potentialParent.level < project.level) {
                        visibleProjectIds.add(potentialParent.project_id);
                    }
                    if (potentialParent.level === 0) break;
                }

                // Also mark all children as visible
                for (let i = index + 1; i < data.projects.length; i++) {
                    const potentialChild = data.projects[i];
                    if (potentialChild.level <= project.level) break;
                    visibleProjectIds.add(potentialChild.project_id);
                }
            }
        });

        return data.projects.filter(project => visibleProjectIds.has(project.project_id));
    }, [data, searchTerm]);

    // Total is now pre-calculated by backend
    const getProjectTotal = useCallback((project) => {
        return project.total_minutes || 0;
    }, []);

    // Format minutes to hours with 15-minute precision (0.25h steps)
    const formatHours = useCallback((minutes) => {
        const hours = minutes / 60;
        const rounded = Math.round(hours * 4) / 4; // Round to nearest 0.25
        return rounded.toFixed(2).replace(/\.?0+$/, ''); // Remove trailing zeros
    }, []);

    // Render loading state
    if (loading) {
        return (
            <div className="modern-breakdown-loading">
                <div className="skeleton-card">
                    <div className="skeleton-header"></div>
                    <div className="skeleton-timeline"></div>
                    <div className="skeleton-row"></div>
                    <div className="skeleton-row"></div>
                    <div className="skeleton-row"></div>
                </div>
            </div>
        );
    }

    // Render error state
    if (error) {
        return (
            <div className="modern-breakdown-error">
                <div className="error-icon">⚠️</div>
                <h4>Fehler beim Laden</h4>
                <p>{error}</p>
                <button className="btn btn-primary" onClick={loadData}>
                    Erneut versuchen
                </button>
            </div>
        );
    }

    // Render empty state
    if (!data || !data.projects || data.projects.length === 0) {
        return (
            <div className="modern-breakdown-empty">
                <div className="empty-icon">📊</div>
                <h4>Keine Daten verfügbar</h4>
                <p>Für dieses Projekt liegen noch keine Aufwandsdaten vor.</p>
            </div>
        );
    }

    // Render project row
    const ProjectRow = ({ project, index }) => {
        const isCollapsed = collapsedProjects.has(project.project_id);
        const total = getProjectTotal(project); // Total for filtered months
        const hasChildren = project.has_children;

        const indent = project.level * 20;

        // Use total across ALL months for budget calculation
        const allMonthsTotal = project.total_all_months || 0;

        // Calculate percentage of budget used (based on ALL months)
        const budgetPercentage = project.maxminutes ? (allMonthsTotal / project.maxminutes) * 100 : null;
        const isOverBudget = budgetPercentage && budgetPercentage > 100;
        const isNearLimit = budgetPercentage && budgetPercentage > 80 && budgetPercentage <= 100;

        return (
            <div
                className={stylelist(
                    "project-row",
                    [project.level === 0, "level-0"],
                    [project.level > 0, "level-child"],
                    [isCollapsed && hasChildren, "collapsed"]
                )}
                style={{ "--indent": `${indent}px` }}
            >
                <div className="project-info" title={project.breadcrumb || project.project_title}>
                    <div className="project-header">
                        <div className="project-title-wrapper">
                            <h3 className="project-title">
                                {project.project_title}
                                {hasChildren && (
                                    <button
                                        className="collapse-btn-inline"
                                        onClick={() => toggleProject(project.project_id)}
                                    >
                                        {isCollapsed ? '▶' : '▼'}
                                    </button>
                                )}
                            </h3>
                        </div>
                        <div className="project-stats-compact">
                            {project.maxminutes > 0 ? (
                                <div className="budget-info">
                                    <div className="budget-main">
                                        <span className="budget-used"><MinutesToTime minutes={allMonthsTotal} /></span>
                                        <span className="budget-separator">/</span>
                                        <span className="budget-max"><MinutesToTime minutes={project.maxminutes} /></span>
                                        <span className={stylelist(
                                            "budget-badge",
                                            [isOverBudget, "badge-danger"],
                                            [isNearLimit, "badge-warning"],
                                            [!isOverBudget && !isNearLimit, "badge-success"]
                                        )}>
                                            {budgetPercentage.toFixed(0)}%
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <span className="stat-value-compact">
                                    <MinutesToTime minutes={allMonthsTotal} />
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="project-timeline">
                    {filteredMonths.map(month => {
                        // Use pre-calculated monthly total with children from backend
                        const monthTotal = project.monthly_totals_with_children?.[month] || 0;
                        const monthBudget = project.maxminutesmonth || 0;
                        const intensity = maxMonthlyValue > 0 ? (monthTotal / maxMonthlyValue) : 0;

                        // Calculate difference from budget
                        const diff = monthBudget > 0 ? monthTotal - monthBudget : null;
                        const isOverBudget = diff !== null && diff > 0;
                        const isUnderBudget = diff !== null && diff < 0;

                        // Calculate background color intensity and text color
                        let bgColor, textColor;
                        if (monthTotal === 0) {
                            bgColor = 'transparent';
                            textColor = '#1a1a1a';
                        } else if (isOverBudget) {
                            const opacity = 0.15 + (Math.abs(diff) / monthBudget * 0.85);
                            bgColor = `rgba(229, 27, 26, ${opacity})`; // Red (over budget)
                            textColor = opacity > 0.6 ? 'rgba(255, 255, 255, 0.95)' : '#1a1a1a'; // White text if dark
                        } else if (isUnderBudget) {
                            bgColor = `rgba(4, 167, 119, ${0.15 + (intensity * 0.85)})`; // Green (under budget)
                            textColor = '#1a1a1a';
                        } else {
                            const opacity = 0.15 + (intensity * 0.85);
                            bgColor = `rgba(229, 27, 26, ${opacity})`; // Slotify Red (no budget set)
                            textColor = opacity > 0.6 ? 'rgba(255, 255, 255, 0.95)' : '#1a1a1a'; // White text if dark
                        }

                        const tooltipText = monthBudget > 0
                            ? `${formatMonth(month).short} ${formatMonth(month).year}: ${(monthTotal / 60).toFixed(1)}h / ${(monthBudget / 60).toFixed(1)}h (${diff > 0 ? '+' : ''}${(diff / 60).toFixed(1)}h)`
                            : `${formatMonth(month).short} ${formatMonth(month).year}: ${(monthTotal / 60).toFixed(1)}h`;

                        return (
                            <div
                                key={month}
                                className={stylelist(
                                    "timeline-cell-compact",
                                    [monthTotal === 0, "cell-empty"],
                                    [isOverBudget, "cell-over-budget"],
                                    [isUnderBudget, "cell-under-budget"]
                                )}
                                style={{ backgroundColor: bgColor, color: textColor }}
                                title={tooltipText}
                            >
                                {monthTotal > 0 && (
                                    <span className="cell-value-compact">
                                        {formatHours(monthTotal)}h
                                        {diff !== null && (
                                            <span className="cell-diff">
                                                {diff > 0 ? '+' : ''}{formatHours(diff)}
                                            </span>
                                        )}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="modern-breakdown">
            <div className="breakdown-header">
                <div className="header-top">
                    <div>
                        <h2 className="breakdown-title">📊 Projekt Breakdown</h2>
                        <p className="breakdown-subtitle">
                            Interaktive Zeitstrahl-Übersicht aller Unterprojekte
                        </p>
                    </div>
                    <button
                        className="btn btn-sm btn-outline-primary refresh-btn"
                        onClick={loadData}
                    >
                        <i className="bi bi-arrow-clockwise"></i>
                    </button>
                </div>

                <div className="controls">
                    <div className="search-box">
                        <i className="bi bi-search"></i>
                        <input
                            type="text"
                            placeholder="Projekte durchsuchen..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button className="clear-search" onClick={() => setSearchTerm("")}>
                                ×
                            </button>
                        )}
                    </div>

                    <div className="period-selector">
                        {[3, 6, 9, 12, 24, 0].map(months => (
                            <button
                                key={months}
                                className={stylelist(
                                    "period-btn",
                                    [monthsToShow === months, "active"]
                                )}
                                onClick={() => setMonthsToShow(months)}
                            >
                                {months === 0 ? 'Seit Beginn' : `${months}M`}
                            </button>
                        ))}

                        <div className="period-divider"></div>

                        <button
                            className={stylelist(
                                "period-btn toggle-btn",
                                [hideEmptyProjects, "active"]
                            )}
                            onClick={() => setHideEmptyProjects(!hideEmptyProjects)}
                            title="Projekte ohne Aufwände ausblenden"
                        >
                            <i className={`bi bi-${hideEmptyProjects ? 'eye-slash' : 'eye'}`}></i>
                            {hideEmptyProjects ? 'Gefiltert' : 'Alle'}
                        </button>
                    </div>

                    <div className="action-btns">

                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => {
                                if (collapsedProjects.size === 0) {
                                    setCollapsedProjects(new Set(data.projects.map(p => p.project_id)));
                                } else {
                                    setCollapsedProjects(new Set());
                                }
                            }}
                        >
                            {collapsedProjects.size === 0 ? 'Alle einklappen' : 'Alle ausklappen'}
                        </button>
                    </div>
                </div>
            </div>

            <div className="breakdown-content">
                <div className="timeline-header">
                    <div className="project-column-header">
                        <span>Projekt</span>
                        <span className="project-count">
                            {filteredProjects.length} {filteredProjects.length === 1 ? 'Projekt' : 'Projekte'}
                        </span>
                    </div>
                    <div className="months-header">
                        {filteredMonths.map(month => {
                            const formatted = formatMonth(month);
                            return (
                                <div key={month} className="month-header">
                                    <div className="month-name">{formatted.short}</div>
                                    <div className="month-year">{formatted.year}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="projects-container">
                    {filteredProjects.length > 0 ? (
                        filteredProjects.map((project, index) => {
                            // Check if any ancestor in the hierarchy is collapsed
                            // With DFS, we check backwards to find the direct parent (level - 1)
                            let shouldHide = false;

                            if (project.level > 0) {
                                // Go backwards to find the direct parent
                                for (let i = index - 1; i >= 0; i--) {
                                    const ancestor = filteredProjects[i];

                                    // If we found a project at our level or higher, stop (not an ancestor)
                                    if (ancestor.level >= project.level) {
                                        continue;
                                    }

                                    // Check if this ancestor is collapsed
                                    if (collapsedProjects.has(ancestor.project_id)) {
                                        shouldHide = true;
                                        break;
                                    }

                                    // Stop when we reach root level
                                    if (ancestor.level === 0) {
                                        break;
                                    }
                                }
                            }

                            if (shouldHide) return null;

                            return (
                                <ProjectRow
                                    key={project.project_id}
                                    project={project}
                                    index={index}
                                />
                            );
                        })
                    ) : (
                        <div className="no-results">
                            <p>Keine Projekte gefunden für "{searchTerm}"</p>
                        </div>
                    )}
                </div>

                {data.monthly_summary && (
                    <div className="summary-footer">
                        <div className="summary-label">Gesamt</div>
                        <div className="summary-timeline">
                            {filteredMonths.map(month => {
                                const total = data.monthly_summary[month] || 0;
                                return (
                                    <div key={month} className="summary-cell">
                                        <MinutesToTime minutes={total} />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MonthlyBreakdown;
